// @flow

import React, { Component } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import classNames from "classnames";

import actionCreators from "@modules/management/actionCreator";
import { isLoading, imagesList, imagesPagination } from "@modules/management/selectors";
import { getUserClientId } from "@modules/account/selectors";
import type { State } from "@store/reducers";
import type { ImageLibraryItem } from "@modules/management/types";
import type { PaginationExtendedType } from "@types";
import { Button, Loader, Pagination, Text, TextField, View } from "@components";

type OwnProps = {
  columns?: number,
  title?: string,
  actions?: React$Node,
  selectedId?: number | null,
  onImageClick: (image: ImageLibraryItem) => void,
};

type PropsFromState = {
  isLoading: boolean,
  list: ImageLibraryItem[],
  pagination: PaginationExtendedType,
  clientId: number | null,
};

type PropsFromDispatch = {
  getImages: typeof actionCreators.GetImages,
};

type Props = {
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
};

type ComponentState = {
  searchInput: string,
  searchTerm: string,
  page: number,
  failed: { [number]: boolean },
};

class ImageLibraryBrowser extends Component<Props, ComponentState> {
  state = {
    searchInput: "",
    searchTerm: "",
    page: 1,
    failed: {},
  };

  componentDidMount() {
    this.props.getImages({ clientId: this.props.clientId });
  }

  onSearchSubmit = () => {
    this.setState({ searchTerm: this.state.searchInput, page: 1 });
    this.props.getImages({
      clientId: this.props.clientId,
      page: 1,
      search: this.state.searchInput,
    });
  };

  onPageChange = (newPage: number | string) => {
    const page = Number(newPage);
    this.setState({ page });
    this.props.getImages({ clientId: this.props.clientId, page, search: this.state.searchTerm });
  };

  onImageError = (id: number) => () => {
    this.setState({ failed: { ...this.state.failed, [id]: true } });
  };

  onTileClick = (image: ImageLibraryItem) => () => {
    this.props.onImageClick(image);
  };

  render() {
    const { t, list, pagination, title, actions, selectedId, columns = 5 } = this.props;
    const { page, searchTerm, failed } = this.state;

    return (
      <React.Fragment>
        {this.props.isLoading && <Loader />}
        <View
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          padding="0 0 1rem 0"
        >
          <View direction="row" justifyContent="space-between" flex="1">
            <View direction="row" alignItems="center" gap={4}>
              {title && <Text type="h5">{title}</Text>}
              <TextField
                placeholder={t("SEARCH_IMAGES")}
                value={this.state.searchInput}
                onChange={e => this.setState({ searchInput: e.target.value })}
              />
              <Button color="primary" label={t("SUBMIT")} onClick={this.onSearchSubmit} />
            </View>
            {actions && <View>{actions}</View>}
          </View>
        </View>
        <View className="image-library-grid" display="grid" gridTemplateColumns={columns} gap={4}>
          {(list.length &&
            list.map((image: ImageLibraryItem) => (
              <View
                key={image.id}
                className={classNames("image-tile", { selected: selectedId === image.id })}
              >
                <View className="thumbnail" role="button" onClick={this.onTileClick(image)}>
                  {!failed[image.id] ? (
                    <img
                      className="img"
                      src={image.thumbnailUrl || image.url}
                      alt={image.filename}
                      onError={this.onImageError(image.id)}
                    />
                  ) : (
                    <Text type="p" className="text" text={image.filename} />
                  )}
                </View>
                <Text type="p" className="caption" title={image.filename}>
                  {image.filename}
                </Text>
              </View>
            ))) || <Text type="p">{searchTerm ? t("NO_IMAGES_FOUND") : t("NO_IMAGES")}</Text>}
        </View>
        <Pagination
          currentPage={page}
          totalPages={pagination.totalPages}
          onChange={this.onPageChange}
        />
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state: State): PropsFromState => ({
  isLoading: isLoading(state),
  list: imagesList(state),
  pagination: imagesPagination(state),
  clientId: getUserClientId(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  getImages: actionCreators.GetImages,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(ImageLibraryBrowser);
